import { useState } from 'react';

const CATEGORIES = [
  'Fraud / Bribery / Corruption',
  'Harassment / Discrimination',
  'Safety Violation',
  'Environmental',
  'Conflicts of Interest',
  'Financial Misconduct',
  'Other',
];

// NsPage: form-page — public-facing whistleblower intake form; every
// select/input/textarea is a real report field, not a filter/toolbar
// control.
export default function EthicsIntakeForm() {
  const cfg          = JSON.parse(sessionStorage.getItem('tenantConfig') || '{}');
  const companyName  = cfg.companyName  || 'Our Company';
  const ethicsEmail  = cfg.ethicsEmail  || null;
  const [category,     setCategory]     = useState('');
  const [description,  setDescription]  = useState('');
  const [isAnonymous,  setIsAnonymous]  = useState(true);
  const [reporterName, setReporterName] = useState('');
  const [reporterEmail,setReporterEmail]= useState('');
  const [submitting,   setSubmitting]   = useState(false);
  const [submitted,    setSubmitted]    = useState(null); // reportNumber on success
  const [error,        setError]        = useState('');

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!description.trim()) { setError('Description is required.'); return; }
    setSubmitting(true);
    try {
      const resp = await fetch('/api/ethics/submit', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category:      category || null,
          description:   description.trim(),
          isAnonymous,
          reporterName:  isAnonymous ? undefined : reporterName || undefined,
          reporterEmail: isAnonymous ? undefined : reporterEmail || undefined,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Submission failed');
      setSubmitted(data.reportNumber);
    } catch (err) {
      setError(err.message || (ethicsEmail
        ? `Submission failed — please try again or contact ${ethicsEmail} directly.`
        : 'Submission failed — please try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--color-bg-primary)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '3rem 1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 540,
        background: 'var(--color-bg-secondary)',
        borderRadius: 10,
        boxShadow: '0 2px 12px rgba(0,0,0,0.09)',
        padding: '2.25rem 2rem',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '1.75rem', textAlign: 'center' }}>
          <div style={{
            fontSize: '1.15rem',
            fontWeight: 700,
            color: 'var(--color-accent)',
            letterSpacing: '0.02em',
            marginBottom: '0.25rem',
          }}>
            {companyName.toUpperCase()}
          </div>
          <h1 style={{
            fontSize: '1.375rem',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            margin: '0 0 0.375rem',
          }}>
            Ethics &amp; Whistleblower Intake
          </h1>
          <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
            All submissions are confidential. Retaliation is prohibited under ROC-POL-ETH-001.
          </p>
          {ethicsEmail && (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', margin: '0.375rem 0 0' }}>
              Reports are reviewed by executive leadership.{' '}
              Contact: <a href={`mailto:${ethicsEmail}`} style={{ color: 'var(--color-accent)' }}>{ethicsEmail}</a>
            </p>
          )}
        </div>

        {submitted ? (
          // ── Success state ──────────────────────────────────────────────────
          <div style={{
            textAlign: 'center',
            padding: '1.5rem 0',
          }}>
            <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✓</div>
            <div style={{
              fontSize: '1rem',
              fontWeight: 600,
              color: 'var(--color-success-text)',
              marginBottom: '0.5rem',
            }}>
              Report received — {submitted}
            </div>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-muted)', margin: 0 }}>
              Your report has been submitted. If you provided contact information, you will hear from us within 48 hours.
            </p>
          </div>
        ) : (
          // ── Form ──────────────────────────────────────────────────────────
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.35rem' }}>
                Category
              </label>
              <select
                className="ns-input"
                value={category}
                onChange={e => setCategory(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">Select a category (optional)</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.35rem' }}>
                Description <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <textarea autoComplete="off"
                className="ns-input"
                rows={6}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Describe the concern in as much detail as possible — dates, names, locations, and any supporting information."
                style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
                required
              />
            </div>

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: 'var(--color-text-primary)', fontWeight: 500 }}>
                <input
                  type="checkbox"
                  checked={isAnonymous}
                  onChange={e => setIsAnonymous(e.target.checked)}
                  style={{ width: 16, height: 16, cursor: 'pointer' }}
                />
                Submit anonymously
              </label>
            </div>

            {!isAnonymous && (
              <div style={{ background: 'var(--color-bg-secondary)', borderRadius: 6, padding: '0.875rem', marginBottom: '1.25rem', display: 'grid', gap: '0.75rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.35rem' }}>
                    Name <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    className="ns-input"
                    type="text"
                    value={reporterName}
                    onChange={e => setReporterName(e.target.value)}
                    placeholder="Your name"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore=""
                  data-form-type="other"
                  data-bwignore=""
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: '0.35rem' }}>
                    Email <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(optional)</span>
                  </label>
                  <input
                    className="ns-input"
                    type="email"
                    value={reporterEmail}
                    onChange={e => setReporterEmail(e.target.value)}
                    placeholder="you@example.com"
                    style={{ width: '100%', boxSizing: 'border-box' }}
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore=""
                  data-form-type="other"
                  data-bwignore=""
                  />
                </div>
              </div>
            )}

            {error && (
              <div style={{ background: 'var(--color-danger-light)', border: '1px solid var(--color-danger-border)', borderRadius: 6, padding: '0.625rem 0.875rem', marginBottom: '1rem', fontSize: '0.875rem', color: 'var(--color-danger)' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              className="ns-btn ns-btn-primary"
              disabled={submitting}
              style={{ width: '100%', justifyContent: 'center' }}
            >
              {submitting ? 'Submitting…' : 'Submit Report'}
            </button>

            {ethicsEmail && (
              <p style={{ marginTop: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)', textAlign: 'center' }}>
                You may also report by email: <a href={`mailto:${ethicsEmail}`} style={{ color: 'var(--color-accent)' }}>{ethicsEmail}</a>
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
